/**
 * Cadrer la piste, quel que soit l'écran.
 *
 * Le problème est réel et se voit tout de suite quand on l'ignore : un portrait de
 * téléphone (9:19,5, soit un rapport de 0,46) est deux fois plus étroit qu'un
 * portrait de tablette. À champ de vision fixe, ce qui tient dans le cadre sur
 * l'un déborde sur l'autre — et un dé hors cadre est un dé illisible, donc un
 * résultat que le joueur ne peut pas lire.
 *
 * On calcule donc le champ vertical à partir de la zone à montrer, en projetant ses
 * coins dans le repère de la caméra. C'est exact, et c'est une fonction pure : elle
 * se teste sans navigateur, contrairement à un réglage fait à l'œil.
 */

import { Matrix4, Vector3 } from "three";

/** Rayon dans lequel les dés doivent s'arrêter, bourrelet exclu. */
export const RAYON_ARRIVEE = 3.55;

/**
 * Où les dés doivent s'immobiliser : un disque, comme le plateau.
 *
 * **Source de vérité unique** : `lancer.ts` refuse les jets qui en sortent, et la
 * caméra se cadre dessus. Les séparer, c'était garantir qu'un réglage de l'un
 * finisse par sortir l'autre du cadre.
 *
 * Le rectangle ci-dessous est le carré circonscrit à ce disque : le cadrage raisonne
 * sur des coins, et voir le carré c'est voir le cercle.
 */
export const ZONE_ARRIVEE = {
  minX: -RAYON_ARRIVEE,
  maxX: RAYON_ARRIVEE,
  minZ: -RAYON_ARRIVEE,
  maxZ: RAYON_ARRIVEE,
} as const;

/** Le volume à garder visible : l'aire d'arrivée des dés, et leur hauteur. */
export interface Zone {
  readonly minX: number;
  readonly maxX: number;
  readonly minZ: number;
  readonly maxZ: number;
  /** Altitude du bas de la zone (la surface du tapis). */
  readonly bas: number;
  /** Altitude du haut de la zone (un dé posé, plus une marge de rebond visible). */
  readonly haut: number;
  /**
   * Rayon, si la zone est un DISQUE centré sur l'origine.
   *
   * Cadrer le carré circonscrit à un plateau rond réserve la place de quatre coins
   * qui n'existent pas : près de 30 % de largeur perdue, et autant de recul inutile
   * sur un écran étroit. Quand ce rayon est donné, on échantillonne le cercle.
   */
  readonly rayon?: number;
}

/** Les points de la zone à garder dans le cadre. */
function pointsACadrer(zone: Zone): ReadonlyArray<readonly [number, number]> {
  if (zone.rayon === undefined) {
    return [
      [zone.minX, zone.minZ], [zone.minX, zone.maxZ],
      [zone.maxX, zone.minZ], [zone.maxX, zone.maxZ],
    ];
  }
  // Vingt-quatre points suffisent : l'écart entre le cercle et le polygone inscrit
  // est alors de moins de deux pour mille du rayon, très en deçà de la marge.
  const points: Array<readonly [number, number]> = [];
  for (let i = 0; i < 24; i++) {
    const angle = (i / 24) * Math.PI * 2;
    points.push([Math.cos(angle) * zone.rayon, Math.sin(angle) * zone.rayon]);
  }
  return points;
}

/**
 * Champ de vision vertical, en degrés, qui fait tenir `zone` dans un écran de
 * rapport `aspect` (largeur / hauteur).
 *
 * `marge` élargit un peu le cadre : 1 collerait les dés au bord.
 */
export function fovPourCadrer(
  zone: Zone,
  aspect: number,
  position: Vector3,
  cible: Vector3,
  marge = 1.12,
): number {
  // Matrice de vue : monde -> repère caméra (elle regarde vers les -z).
  const vue = new Matrix4().lookAt(position, cible, new Vector3(0, 1, 0));
  vue.setPosition(position);
  vue.invert();

  const point = new Vector3();
  let tangenteRequise = 0;

  for (const [x, z] of pointsACadrer(zone)) {
    for (const y of [zone.bas, zone.haut]) {
      {
        point.set(x, y, z).applyMatrix4(vue);
        const profondeur = -point.z;
        // Un coin derrière la caméra n'a pas de cadrage possible : on l'ignore
        // plutôt que de renvoyer un champ absurde.
        if (profondeur <= 1e-3) continue;
        // Contrainte verticale, et contrainte horizontale ramenée au vertical.
        tangenteRequise = Math.max(
          tangenteRequise,
          Math.abs(point.y) / profondeur,
          Math.abs(point.x) / profondeur / aspect,
        );
      }
    }
  }

  if (tangenteRequise === 0) return 46;
  const fov = 2 * Math.atan(tangenteRequise * marge) * (180 / Math.PI);
  return Math.max(28, fov);
}

/**
 * Champ de vision au-delà duquel on préfère reculer que continuer d'ouvrir. Un
 * grand angle déforme les dés au bord et donne une impression de fisheye.
 */
const FOV_CONFORTABLE = 62;

/** Placement et champ retenus pour un écran donné. */
export interface Cadrage {
  readonly position: Vector3;
  readonly cible: Vector3;
  readonly fov: number;
}

/**
 * Cadre la piste sur n'importe quel écran, en reculant plutôt qu'en déformant.
 *
 * Sur un portrait de téléphone (rapport 0,46), garder la piste entière dans le
 * cadre demanderait plus de 90° de champ depuis la position nominale : les dés du
 * bord seraient étirés. On éloigne donc la caméra le long de son axe jusqu'à ce
 * qu'un champ confortable suffise. Les dés paraissent un peu plus petits — c'est de
 * la géométrie, pas un réglage : un écran deux fois plus étroit montre deux fois
 * moins de largeur à distance égale.
 */
export function cadrerPiste(zone: Zone, aspect: number): Cadrage {
  const cible = new Vector3(...VUE.cible);
  const nominale = new Vector3(...VUE.position);
  const axe = nominale.clone().sub(cible);

  const tangenteConfortable = Math.tan((FOV_CONFORTABLE * Math.PI) / 360);
  let recul = 1;
  let position = nominale.clone();
  let fov = fovPourCadrer(zone, aspect, position, cible);

  // Deux ou trois itérations suffisent : reculer d'un facteur k divise la tangente
  // requise par ~k, mais pas exactement (la zone n'est pas ponctuelle).
  for (let i = 0; i < 6 && fov > FOV_CONFORTABLE + 0.5; i++) {
    const tangenteRequise = Math.tan((fov * Math.PI) / 360);
    recul *= Math.max(1.02, tangenteRequise / tangenteConfortable);
    position = cible.clone().add(axe.clone().multiplyScalar(recul));
    fov = fovPourCadrer(zone, aspect, position, cible);
  }

  // On rend le champ réellement requis, pas le plafond : le rogner de quelques
  // dixièmes suffirait à sortir un dé du cadre, et c'est tout l'inverse du but.
  return { position, cible, fov };
}

/**
 * Jusqu'où porte le décor : le plateau et son bourrelet.
 *
 * Cadrer le plateau ENTIER, et non la seule aire d'arrivée, est ce qui remet les dés
 * à leur taille. L'ancien cadrage serrait sur trois dés et demi de large : un dé
 * occupait le tiers de l'écran, et Olivier l'a vu tout de suite.
 */
const RAYON_BORD = 4.7;

/**
 * La zone effectivement cadrée : le plateau, sur la hauteur d'un dé posé. On ne cadre
 * pas l'apogée du jet — un dé qui sort brièvement par le haut est normal et se
 * comprend ; un dé qui s'arrête hors cadre, non.
 */
export const ZONE_VISIBLE: Zone = {
  minX: -RAYON_BORD,
  maxX: RAYON_BORD,
  minZ: -RAYON_BORD,
  maxZ: RAYON_BORD,
  bas: -0.5,
  haut: 0.7,
  rayon: RAYON_BORD,
};

/** Placement de la caméra : 3/4 plongeante, comme un joueur penché sur le comptoir. */
export const VUE = {
  position: [0, 7.4, 7.0] as const,
  /*
   * Visé sur le CENTRE du plateau, et non sur un barycentre d'arrivées comme au
   * temps de la piste rectangulaire : le geste attaque désormais de n'importe quel
   * point du pourtour, donc les dés s'arrêtent un peu partout, sans côté privilégié.
   */
  cible: [0, -0.4, 0] as const,
} as const;
